<script setup>
import {
  computed, inject, reactive, ref,
} from 'vue';
import InputFechaHora from '@/components/cifra/InputFechaHora.vue';
import SelectorNumerico from '@/components/inputs/SelectorNumerico.vue';
import InputNumerico from '@/components/inputs/InputNumerico.vue';
import { onlyISODate } from '@/libs/dates';
import { add } from 'date-fns';
import { templateRef } from '@vueuse/core';
import { waitForTurboFormSubmissionResult } from '@/libs/http';
import BotonGuardar from '@/components/inputs/BotonGuardar.vue';

const I18n = inject('I18n');

const datosConfirmar = {
  type: 'warning',
  title: I18n.t('atencion!'),
  text: I18n.t('fraccionar_aviso'),
  confirmButtonText: I18n.t('Aceptar'),
};

const props = defineProps({
  idsCobros: { type: Array, required: true },
  fechaOrigen: { type: Date, default: () => new Date() },
  importeFraccionar: { type: Number, default: null },
  modelValue: { type: Array, default: () => [] },
});

const formulario = templateRef('formulario');
const numFracciones = ref(2);
const numCobros = computed(() => props.idsCobros.length);
const edicionImportes = computed(() => !!props.importeFraccionar);
const guardando = ref(false);

function importePorFraccion() {
  if (!edicionImportes.value) {
    return undefined;
  }
  if (!props.importeFraccionar) {
    return 0;
  }
  return parseFloat((props.importeFraccionar / numFracciones.value).toFixed(2));
}

const fracciones = computed(() => {
  const res = [];
  let importeComputado = 0;
  for (let i = 0; i < numFracciones.value; i += 1) {
    const importeFraccion = importePorFraccion();
    importeComputado += importeFraccion;

    res.push({
      fecha: onlyISODate(add(props.fechaOrigen, { months: i })),
      importe: importeFraccion,
    });
  }

  // Según las divisiones que se hagan (por ejemplo 100 / 3) pueden quedar descuadres
  // que vamos a sumarlos a la última fracción
  if (props.importeFraccionar && importeComputado !== props.importeFraccionar) {
    res[res.length - 1].importe += props.importeFraccionar - importeComputado;
  }

  return reactive(res);
});

async function guardar() {
  const promise = waitForTurboFormSubmissionResult(formulario.value);
  guardando.value = true;
  try {
    formulario.value.requestSubmit();
    await promise;
  } catch (e) {
    // Ya mostramos el error con un flash desde el servidor
  } finally {
    guardando.value = false;
  }
}

</script>

<template>
  <form
    ref="formulario"
    action="/gestion_economica/cobros/fraccionar_cobros"
    method="post"
  >
    <input type="hidden" name="fraccionar_cobros[ids]" :value="idsCobros.join(' ')" />
    <p class="text-muted text-right">
      {{ $t('seleccionados_n_cobros_fraccionar', { count: numCobros}) }}
    </p>
    <div class="form-group">
      <label class="control-label inline m-r">
        {{ $t('numero_fracciones') }}:
      </label>
      <SelectorNumerico
        v-model="numFracciones"
        size="sm"
        :min="2"
        :max="12"
        class="inline va-m"
      ></SelectorNumerico>
    </div>
    <table class="table">
      <thead>
        <tr>
          <th></th>
          <th>{{ $t('vencimiento_fecha') }}</th>
          <th v-if="edicionImportes">
            {{ $t('Importe') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(fraccion, index) in fracciones" :key="index">
          <td class="nowrap">
            Fracción {{ index }}
          </td>
          <td>
            <InputFechaHora
              :name="`fraccionar_cobros[fracciones[][fecha_vencimiento]]`"
              :model-value="fraccion.fecha"
            />
          </td>
          <td v-if="edicionImportes">
            <InputNumerico
              v-model="fraccion.importe"
              class="form-control text-right"
              input-name="fraccionar_cobros[fracciones[][importe]]"
              configuracion="precio"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <div class="form-actions">
      <button
        type="button"
        class="btn btn-default"
        data-dismiss="modal"
        :disabled="guardando"
      >
        {{ $t('Cancelar') }}
      </button>
      <BotonGuardar
        v-confirmar="datosConfirmar"
        :submitting="guardando"
        @confirmado="guardar"
      >
        {{ $t('Guardar') }}
      </BotonGuardar>
    </div>
  </form>
</template>
